import {
  type ApolloError,
  type FetchResult,
  isApolloError,
  NetworkStatus,
  type OperationVariables,
  type QueryResult,
} from "@apollo/client";
import { t } from "@lingui/core/macro";
import { EMPTY_STRING } from "@regrello/core-utils";
import type { ErrorCode, ErrorDetails, SubmissionValidationResult } from "@regrello/graphql-api";
import { DEBOUNCE_TIMEOUT, MUTATION_TRACKING_TIMEOUT } from "../constants/globalConstants";
import { mutationTracker } from "../ui2/app/init/_internal/mutationTracker";

// (clewis): These function are new as of Aug 18 and haven't been rigorously battle-tested to verify
// correctness with edge cases like refetching, polling, etc. Use them cautiously.

export async function awaitCorrectlyTypedAwaitedMutationResults(
  promises: Array<string | Promise<FetchResult<unknown>>>,
): Promise<FetchResult[]> {
  // (dosipiuk): Type cast is needed due to invalid inference from generated apollo types
  //
  // See: https://github.com/apollographql/apollo-client/issues/9292
  const results = (await Promise.all(promises)) as FetchResult[];
  return results;
}

/**
 * Returns `true` if the provided Apollo Client query result is in the initial loading state.
 *
 * `initial loading` means that query is in-flight and has no `current` or `previous` data.
 *
 * `previous data` is set if we change the `variables` on query, where we want to show previous data until new comes in.
 *
 * `current data` is set if this query executed previously and has some data in cache.
 */
export function isQueryInitialLoading<TData, TVariables extends OperationVariables>(
  queryResult: QueryResult<TData, TVariables>,
) {
  // (dospiuk): Since apollo does not provide a way to determine if a query is in initial loading state,
  // we have to do it ourselves.
  return queryResult.loading && queryResult.previousData == null && queryResult.data == null;
}

/**
 * Returns `true` if the provided Apollo Client query result has not been called or is still
 * initially loading.
 */
export function isQueryResultLoading<TData, TVariables extends OperationVariables>(
  queryResult: QueryResult<TData, TVariables>,
) {
  return !queryResult.called || queryResult.loading;
}

/**
 * Returns `true` if the provided Apollo Client query result has been called and finished loading -
 * either successfully (with data) or unsuccessfully (with an error).
 */
export function isQueryResultReady<TData, TVariables extends OperationVariables>(
  queryResult: QueryResult<TData, TVariables>,
) {
  return (
    !isQueryResultLoading(queryResult) &&
    (queryResult.networkStatus === NetworkStatus.ready || queryResult.networkStatus === NetworkStatus.error)
  );
}

/**
 * Returns `true` if the provided Apollo Client query result has returned an error.
 */
export function isQueryResultError<TData, TVariables extends OperationVariables>(
  queryResult: QueryResult<TData, TVariables>,
) {
  // (clewis): This isn't the most beautiful - or even verifiably correct - check, but it's what we
  // tend to check nowadays. Probably deserves more investigation (e.g., into networkStatus).
  return !isQueryResultLoading(queryResult) && (queryResult.error != null || queryResult.data == null);
}

/** Returns `true` if at least one result in the provided array of fetch results has failed. */
// (dosipiuk): Type cast is needed due to invalid inference from generated apollo types
//
// See: https://github.com/apollographql/apollo-client/issues/9292
export function isSomeMutationResultFailed(results: FetchResult[]): boolean {
  return results.some((result) => result.errors != null);
}

/**
 * Returns `true` if the provided GraphQL response error was considered a user-validation error by
 * the backend. This distinction affects how the error is reported to our monitoring tools.
 */
export function isValidationApolloError({ graphQLErrors }: Pick<ApolloError, "graphQLErrors">): boolean {
  const errorCode = getRegrelloErrorCodeFromApolloError({ graphQLErrors });
  return errorCode?.startsWith("ValidationErrorCode") ?? false;
}

/**
 * Returns the freshest data possible from the provided query result. The `getPreviousId` and
 * `currentId` parameters help determine whether the previous data and current data correspond to
 * the same entity or separate entities. If they're different entities, then the function won't fall
 * back to the `previousData`.
 */
export function getDataOrFallbackToPreviousData<TData, TVariables extends OperationVariables>(
  queryResult: QueryResult<TData, TVariables>,
  getPreviousId: (previousData: TData | undefined) => string | number | null | undefined,
  currentId: string | number | null | undefined,
): TData | undefined {
  if (queryResult.data != null) {
    return queryResult.data;
  }

  const previousId = getPreviousId(queryResult.previousData);
  const shouldIgnorePreviousData = previousId != null && currentId != null && previousId !== currentId;
  if (queryResult.error == null && !shouldIgnorePreviousData) {
    return queryResult.previousData;
  }
  return undefined;
}

/**
 * Returns the Regrello error code from the provided `ApolloError` if an error code is present, or
 * `undefined` otherwise. If for some reason there are two error codes, this function will return
 * only the first one.
 */
export function getRegrelloErrorCodeFromApolloError({
  graphQLErrors,
}: Pick<ApolloError, "graphQLErrors">): string | undefined {
  if (graphQLErrors.length === 0) {
    return undefined;
  }
  const errorCode = graphQLErrors[0].extensions.code;
  if (typeof errorCode !== "string") {
    return undefined;
  }
  return errorCode;
}

function isErrorDetails(obj: unknown): obj is ErrorDetails {
  if (typeof obj !== "object" || obj === null) {
    return false;
  }

  const { errorcode } = obj as ErrorDetails;
  if (typeof errorcode !== "string") {
    return false;
  }

  const parameter = (obj as ErrorDetails).parameter;
  if (!Array.isArray(parameter) || parameter === null) {
    return false;
  }

  return true;
}

function assertNever(x: never): never {
  throw new Error(`Unhandled case: ${x}`);
}

function getParameter(parameters: ErrorDetails["parameter"], key: string) {
  for (const parameter of parameters) {
    if (parameter.key === key) {
      return parameter.value;
    }
  }

  return undefined;
}

function getNumberParameter(parameters: ErrorDetails["parameter"], key: string) {
  const value = getParameter(parameters, key);

  if (typeof value === "number") {
    return value;
  }

  return undefined;
}

function getBooleanParameter(parameters: ErrorDetails["parameter"], key: string) {
  const value = getParameter(parameters, key);

  if (typeof value === "boolean") {
    return value;
  }

  return undefined;
}

function getStringParameter(parameters: ErrorDetails["parameter"], key: string) {
  const value = getParameter(parameters, key);

  if (typeof value === "string") {
    return value;
  }

  return String(value);
}

/**
 * Returns the Regrello "FriendlyMessageObject" error code from the provided `ApolloError` if an
 * error code is present, or `undefined` otherwise. If for some reason there are more than one error
 * codes, this function will return only the first one.
 */
export function maybeGetRegrelloErrorFriendlyErrorCodeFromApolloError(error: ApolloError): ErrorCode | undefined {
  const { graphQLErrors } = error;
  if (graphQLErrors.length === 0) {
    return undefined;
  }

  const errorDetails = graphQLErrors[0].extensions.errorDetails;
  if (!isErrorDetails(errorDetails)) {
    return undefined;
  }
  return errorDetails.errorcode;
}

/**
 * Returns the submission validation result from the provided `ApolloError` if it exists, or
 * `undefined` otherwise. So far, this type can only be returned in errors from the `SubmitActionItem`
 * and `ApproveActionItem` mutations. This is thus a very special case.
 */
export function maybeGetSubmissionValidationResultFromApolloError(
  error: ApolloError,
): SubmissionValidationResult | undefined {
  const extensions = error.graphQLErrors[0]?.extensions;
  if (extensions == null) {
    return undefined;
  }

  const errorDetails = extensions.errorDetails as ErrorDetails | undefined;
  if (errorDetails == null) {
    return undefined;
  }

  if (errorDetails.errorcode !== "ActionItemHasValidationErrors") {
    return undefined;
  }

  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  // biome-ignore lint/suspicious/noExplicitAny: This is a bastardization of the parameters API in which we intentionally return an unsupported object type.
  const maybeValidationResult = (errorDetails.parameter as any)?.validationResult;

  if (
    maybeValidationResult == null ||
    typeof maybeValidationResult !== "object" ||
    !("isValid" in maybeValidationResult) ||
    !("tableResults" in maybeValidationResult) ||
    !Array.isArray(maybeValidationResult.tableResults)
  ) {
    return undefined;
  }

  return maybeValidationResult as SubmissionValidationResult;
  /* eslint-enable @typescript-eslint/no-unsafe-member-access */
}

/**
 * Returns the Regrello "FriendlyMessageObject" as translated string (if it exists) from the provided `ApolloError`. If
 * for some reason there are more than one error, this function will use only the first one.
 */
export function getRegrelloErrorFriendlyMessageFromApolloError(error: Error | string): string | undefined {
  if (typeof error !== "object" || !isApolloError(error)) {
    return undefined;
  }

  const { graphQLErrors } = error;

  if (graphQLErrors.length === 0) {
    return undefined;
  }

  const errorDetails = graphQLErrors[0].extensions.errorDetails;

  if (!isErrorDetails(errorDetails)) {
    // Fallback to the old friendly message format
    const friendlyMessage = graphQLErrors[0].extensions.friendlyMessage;

    if (typeof friendlyMessage !== "string") {
      return undefined;
    }

    return friendlyMessage;
  }

  const errorCode = errorDetails.errorcode;
  switch (errorCode) {
    case "FileExceedsSize": {
      const maxFileSize = getNumberParameter(errorDetails.parameter, "maxFileSize" as const);

      if (maxFileSize == null) {
        return t`Uploaded file exceeded file size limit.`;
      }

      const fileSizeInKilobyte = Math.round(maxFileSize / 1000);
      return t`Uploaded file cannot exceed ${fileSizeInKilobyte} kB.`;
    }

    case "DocusignTeamsCannotBeAssigned": {
      return t`The document cannot be sent to a team for signature via DocuSign. Please restart the task or review the workflow level data that is sending in the signor(s) and only include people.`;
    }

    case "CannotAssignScimRole": {
      return t`Cannot manually assign a role that is managed by SCIM. All assignments must be done via SCIM.`;
    }

    case "NameConflict": {
      const objectName = getStringParameter(errorDetails.parameter, "objectName" as const) as "role" | "field";

      if (objectName === "role") {
        return t`A role with the provided name already exists. Please choose another name.`;
      }

      if (objectName === "field") {
        return t`A field with the provided name already exists. Please choose another name.`;
      }

      return t`A ${objectName} with the provided name already exists. Please choose another name.`;
    }

    case "CannotPublishDueToWorkflowTemplateRelationMismatch": {
      return t`Ensure that each linked blueprint has either an assigned owner or matching start workflow permissions.`;
    }

    case "BlueprintContainsUnmappedFormFields": {
      const taskTemplateName = getStringParameter(errorDetails.parameter, "actionItemTemplateName" as const);
      const fieldName = getStringParameter(errorDetails.parameter, "fieldName" as const);
      return t`Cannot publish Blueprint because the task ${taskTemplateName} has an unmapped required field ${fieldName}. Please update the mapping and try again.`;
    }

    case "BlueprintFormNameCannotBeEmpty": {
      return t`Cannot publish Blueprint because one of the forms has an empty name. Please name your form and try again.`;
    }

    case "BlueprintFormInvalidSection": {
      const formName = getStringParameter(errorDetails.parameter, "formName" as const);
      const formSection = getStringParameter(errorDetails.parameter, "formSection" as const);
      return t`Cannot publish Blueprint because the form ${formName} contains an invalid section. Please remove the section ${formSection} and try again.`;
    }

    case "BlueprintFormHasNoSections": {
      const formName = getStringParameter(errorDetails.parameter, "formName" as const);
      return t`Cannot publish Blueprint because the form ${formName} has no sections. Please add a section and try again.`;
    }

    case "BlueprintFormHasEmptyColumns": {
      const formName = getStringParameter(errorDetails.parameter, "formName" as const);
      return t`Cannot publish Blueprint because the form ${formName} has empty columns. Please remove empty columns and try again.`;
    }

    case "BlueprintFormHasEmptySections": {
      const formName = getStringParameter(errorDetails.parameter, "formName" as const);
      return t`Cannot publish Blueprint because the form ${formName} has empty sections. Please remove empty sections and try again.`;
    }

    case "BlueprintFormHasEmptyTables": {
      const formName = getStringParameter(errorDetails.parameter, "formName" as const);
      return t`Cannot publish Blueprint because the form ${formName} has empty tables. Please remove empty tables and try again.`;
    }

    case "FormNameIsMustBeUniqueInBlueprint": {
      const name = getStringParameter(errorDetails.parameter, "name" as const);
      return t`Cannot publish Blueprint because the form name ${name} is used more than once. Please rename your form and try again.`;
    }

    case "FormNameCannotBeEmpty": {
      return t`Form name cannot be empty, please name your form and try again.`;
    }

    case "FormNameIsAlreadyInUse": {
      const name = getStringParameter(errorDetails.parameter, "name" as const);
      return t`Form version name ${name} is already in use, please rename your form and try again.`;
    }

    case "FormHasEmptySections": {
      return t`Cannot publish a form that has empty sections. Please remove empty sections and try again.`;
    }

    case "FormHasNoSections": {
      return t`Cannot publish a form that has no sections. Please add a section and try again.`;
    }

    case "BlueprintIntakeFormHasMoreThanOneTable": {
      return t`Cannot publish a form that has more than one table on the intake form. Please remove table sections and try again.`;
    }

    case "MultiplePartyNotAllowed": {
      const fieldName = getStringParameter(errorDetails.parameter, "fieldName" as const);
      return t`The ${fieldName}field is configured to only accept a single party. Multiple parties are not allowed.`;
    }

    case "CannotDeleteRoleField": {
      const fieldName = getStringParameter(errorDetails.parameter, "fieldName" as const);
      return t`The ${fieldName} field cannot be deleted until the role it is associated with is deleted.`;
    }

    case "CannotRequestRoleFieldMultipleTimes": {
      const fieldName = getStringParameter(errorDetails.parameter, "fieldName" as const);
      return t`The ${fieldName} field cannot be requested more than once in a workflow or blueprint.`;
    }

    case "CannotRequestRoleFieldMultipleTimesViaForm": {
      return t`Roles cannot be requested more than once in a workflow or blueprint (including forms).`;
    }

    case "ScopedNonAdminPeopleAndTeamsQueriesNotAllowed": {
      return t`Specific people and teams queries are not accessible to non-admins.`;
    }

    case "PartyCannotFillRoleFieldWithID": {
      const roleName = getStringParameter(errorDetails.parameter, "roleName" as const);
      return t`The supplied person does not belong to the role ${roleName}.`;
    }

    case "CannotModifyPublishedForm": {
      return t`Form is already published and cannot be modified.`;
    }

    case "CannotModifyFormVersionUsedInPublishedBlueprint": {
      return t`Form version is being used in a published blueprint and cannot be modified.`;
    }

    case "CannotCreateFormOnPublishedBlueprint": {
      return t`Cannot create a new form version on a published blueprint.`;
    }

    case "ExternalUserCannotCreateActionItemTemplateWithFieldInstances": {
      return t`External users cannot create tasks with field instances.`;
    }

    case "ExternalUserCannotCreateAutomatedTasks": {
      return t`External users cannot create automated tasks.`;
    }

    case "CannotAssignExternalUserOrTeamAsWorkflowOwner": {
      return t`The workflow owner was set to an external user or team. Please set the owner to an internal user or team and try again.`;
    }

    case "CannotUpdateWorkflowOwner": {
      return t`Cannot update workflow owner since it is controlled by the parent workflow.`;
    }

    case "CannotViewFormVersion": {
      return t`User does not have permission to view this form version.`;
    }

    case "FormIsLocked": {
      return t`Form is currently being modified by another user. Please try again later.`;
    }

    case "ActionItemHasValidationErrors": {
      // Does not show an error. Rather, this cues the frontend to show inline validation errors
      // after an attempted task submission.
      return EMPTY_STRING;
    }

    case "ActionItemTemplateIsLocked": {
      return t`The task is currently being modified by another user. Please try again later.`;
    }

    case "CannotModifyFormVersionWithActionItems": {
      return t`Cannot modify form version with tasks attached. Please create a new form version.`;
    }

    case "CannotDeleteInUseForm": {
      return t`Cannot delete a form that is in use.`;
    }

    case "CannotEditFormFromCompletedWorkflow": {
      return t`Cannot edit a form from completed workflows.`;
    }

    case "FormVersionNotFound": {
      return t`Form version not found.`;
    }

    case "UnknownDirectiveField": {
      const fieldName = getStringParameter(errorDetails.parameter, "fieldName" as const);
      return t`${fieldName} is not a known field and could not be validated.`;
    }

    case "CannotActionFormCreatedByAnotherUser": {
      const action = getStringParameter(errorDetails.parameter, "action" as const);
      return t`Cannot ${action} a form created by another user.`;
    }

    case "ExternalUserCannotSaveCollaborationWithUnrelatedUser": {
      const offendingPartyID = getStringParameter(errorDetails.parameter, "offendingPartyID" as const);
      return t`User does not have permission to add party ID '${offendingPartyID}' as a workflow collaborator`;
    }

    case "FieldMustBeFilledOutBeforeSubmittingTask": {
      const fieldName = getStringParameter(errorDetails.parameter, "fieldName" as const);
      return t`Field '${fieldName}' must be filled out before submitting the task.`;
    }

    case "UserNotAllowedToInviteUsers": {
      const missingEmail = getStringParameter(errorDetails.parameter, "missingEmail" as const);
      return t`The user ${missingEmail} does not exist and you do not have permission to invite new users.`;
    }

    case "CannotShareNonCompliantWorkflowTemplate": {
      return t`Cannot publish a blueprint variant that is not compliant with the standard blueprint.`;
    }

    case "CannotShareWorkflowTemplateWithoutVersionNotes": {
      const isChildBlueprint = getBooleanParameter(errorDetails.parameter, "isChildBlueprint" as const);
      const blueprintName = getStringParameter(errorDetails.parameter, "blueprintName" as const);

      if (isChildBlueprint) {
        return t`The blueprint cannot be published with missing version notes for the linked blueprint ${blueprintName}. Editors of the blueprint can fill in version notes by going to the About tab and selecting Edit details.`;
      }

      return t`The blueprint ${blueprintName} cannot be published with missing version notes. Editors of the blueprint can fill in version notes by going to the About tab and selecting Edit details.`;
    }

    case "NestedConditionGroupsNotSupported": {
      return t`Nested condition groups are not supported.`;
    }

    case "VersionNotesMaxLengthExceeded": {
      const maxLength = getNumberParameter(errorDetails.parameter, "maxLength" as const) ?? "?";
      return t`The provided version notes are too long, please try again with fewer than ${maxLength} characters.`;
    }

    case "UserDoesNotHavePermissionToGenerateBlueprintsWithoutProvidingDocuments": {
      return t`You don't have permission to generate blueprints without providing documents.`;
    }

    case "Forbidden": {
      return t`You don't have permission to perform this action.`;
    }

    case "FieldMappingForAutomationTaskMustBeCompleted": {
      return t`Field mapping for automation task must be completed.`;
    }

    case "MultiUserFieldMustBeUnique": {
      return t`People field must contain unique values.`;
    }

    case "ColumnSectionFullyOccupied": {
      return t`Cannot add fields to fully occupied columns.`;
    }

    case "TableSectionFullyOccupied": {
      const maxColumns = getNumberParameter(errorDetails.parameter, "maxColumns" as const) ?? "?";
      return t`Tables only support up to ${maxColumns} columns.`;
    }

    case "DeleteRoleWhilePublished": {
      return t`Cannot delete a role while it is still used in published workflow templates.`;
    }

    case "UpdateUserFieldFailed": {
      return t`Unable to update user field instances.`;
    }

    case "CannotRemoveSelfFromAdminRole": {
      return t`You can't remove yourself from the Admin role.`;
    }

    case "InsufficientPermissionsToInviteUser": {
      return t`You don't have permission to invite this user.`;
    }

    case "InsufficientPermissionsToAssignRoles": {
      return t`You don't have permission to assign roles to another user.`;
    }

    case "MaxResultsOutOfRange": {
      const min = getNumberParameter(errorDetails.parameter, "min" as const) ?? "?";
      const max = getNumberParameter(errorDetails.parameter, "max" as const) ?? "?";
      return t`Max results must be between ${min} and ${max}.`;
    }

    case "DocumentDoesNotExist": {
      return t`A provided document is unavailable.`;
    }

    case "BlueprintTransferFailedToFetchData": {
      const dataType = getStringParameter(errorDetails.parameter, "dataType" as const);
      return t`Failed to fetch the ${dataType} in this workspace.`;
    }
    case "BlueprintTransferFailedToFindObject": {
      const objectType = getStringParameter(errorDetails.parameter, "objectType" as const);
      return t`Failed to find the ${objectType} in this workspace.`;
    }
    case "BlueprintTransferFailedToImportBlueprint": {
      const blueprintName = getStringParameter(errorDetails.parameter, "blueprintName" as const);
      return t`Failed to import the blueprint ${blueprintName}.`;
    }
    case "BlueprintTransferFailedToImportBlueprintDefault":
      return t`Failed to import the blueprint.`;
    case "BlueprintTransferInProgressDraft": {
      const blueprintName = getStringParameter(errorDetails.parameter, "blueprintName" as const);
      return t`Failed to import blueprint because ${blueprintName} already has a draft in progress and cannot be updated.`;
    }
    case "BlueprintTransferInvalidRexFile":
      return t`Something went wrong with the uploaded .rex file.`;
    case "BlueprintTransferInvalidStandardBlueprint": {
      const blueprintName = getStringParameter(errorDetails.parameter, "blueprintName" as const);
      return t`Failed to import the blueprint ${blueprintName}.`;
    }
    case "BlueprintTransferInvalidVersion":
      return t`Something went wrong with the uploaded .rex file because it uses an old format.`;
    case "BlueprintTransferNotLatestPublishedVersion": {
      const blueprintName = getStringParameter(errorDetails.parameter, "blueprintName" as const);
      return t`Failed to import the blueprint ${blueprintName} because the selected blueprint is not the latest published version.`;
    }
    case "CannotShareWorkflowTemplateFormFieldMappingBroken": {
      return t`Cannot share a blueprint with a form field mapping that is broken.`;
    }
    case "BlueprintTransferFileExceedsIndividualSize": {
      const maxFileSize = getNumberParameter(errorDetails.parameter, "maxFileSize" as const);
      const fileName = getStringParameter(errorDetails.parameter, "fileName" as const);

      if (maxFileSize == null) {
        return t`The .rex file is too large to download. Remove the document ${fileName} from the blueprint and try again. Contact a Regrello admin if the issue persists.`;
      }
      const fileSizeInMegabyte = Math.round(maxFileSize / 1_000_000);

      return t`The .rex file is too large to download. Remove the document ${fileName} from the blueprint and try again. Max document size is ${fileSizeInMegabyte} MB. Contact a Regrello admin if the issue persists.`;
    }
    case "BlueprintTransferFileExceedsTotalSize": {
      const maxFileSize = getNumberParameter(errorDetails.parameter, "maxFileSize" as const);

      if (maxFileSize == null) {
        return t`The .rex file is too large to download. Remove some of the documents from the blueprint and try again. Contact a Regrello admin if the issue persists.`;
      }

      const fileSizeInMegabyte = Math.round(maxFileSize / 1_000_000);

      return t`The .rex file is too large to download. Remove some of the documents from the blueprint and try again. Max combined document size is ${fileSizeInMegabyte} MB. Contact a Regrello admin if the issue persists.`;
    }
    default: {
      assertNever(errorCode as never);
    }
  }
}

/**
 * Waits until the specified mutation operation names are no longer in flight. This is useful when
 * the mutation you want to wait for lives very far away from the place where you want to invoke the
 * next mutation or if the dependency is not obvious to resolve by prop drilling.
 *
 * @param operationNames - Array of operation names to wait for.
 * @returns Promise that resolves when all are done.
 */
export async function waitForMutationsToFinish(operationNames: string[]): Promise<void> {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const check = async () => {
      while (operationNames.some((name) => mutationTracker.getCount(name) > 0)) {
        if (Date.now() - start > MUTATION_TRACKING_TIMEOUT) {
          reject(new Error("Timed out when waiting for mutations to finish."));
          return;
        }
        await new Promise((r) => setTimeout(r, DEBOUNCE_TIMEOUT));
      }
      resolve();
    };
    void check();
  });
}
